import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { RotateCcw, XCircle, Package } from "lucide-react";
import { useNotification } from "../../hooks/useNotification";
import {
  cancelMyStockRequest,
  getMyStockRequests,
} from "../../services/api";
import "./StockRequest.css";

const PAGE_SIZE = 20;

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

/**
 * Displays the promoter's own stock requests.
 *
 * Rejected or cancelled requests can be resubmitted as new requests.
 * The form is prefilled and revalidated against current stock.
 * The original request remains unchanged for audit history.
 */

function StockRequestHistory({ outletId, onRequestAgain }) {
  const { t } = useTranslation();
  const { showError, showSuccess } = useNotification();

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [cancellingId, setCancellingId] = useState(null);
  // The request awaiting cancel confirmation (null = dialog closed).
  const [cancelTarget, setCancelTarget] = useState(null);
  // Default to the current outlet; let the promoter widen it to every outlet.
  const [scope, setScope] = useState("outlet");

  // If there is no active outlet, there is nothing to scope to — show all.
  const effectiveScope = outletId ? scope : "all";

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const response = await getMyStockRequests({
        limit: PAGE_SIZE,
        outletId: effectiveScope === "outlet" ? outletId : undefined,
      });
      setRequests(Array.isArray(response?.data) ? response.data : []);
    } catch (fetchError) {
      const message =
        fetchError?.response?.data?.error ||
        t("se.stockRequest.history.failedToLoad", {
          defaultValue: "Failed to load your stock requests.",
        });
      setError(message);
      setRequests([]);
    } finally {
      setLoading(false);
    }
  }, [t, outletId, effectiveScope]);

  useEffect(() => {
    load();
  }, [load]);

  const handleConfirmCancel = async () => {
    if (!cancelTarget) return;
    const request = cancelTarget;
    try {
      setCancellingId(request.stock_request_id);
      await cancelMyStockRequest(request.stock_request_id);
      showSuccess(
        t("se.stockRequest.history.cancelled", {
          defaultValue: "Request cancelled.",
        }),
      );
      setCancelTarget(null);
      load();
    } catch (cancelError) {
      showError(
        cancelError?.response?.data?.error ||
          t("se.stockRequest.history.cancelFailed", {
            defaultValue: "Could not cancel this request.",
          }),
      );
    } finally {
      setCancellingId(null);
    }
  };

  // Scope toggle: only meaningful when there is a current outlet to scope to.
  const scopeToggle = outletId ? (
    <div className="sr-history-scope" role="tablist">
      <button
        type="button"
        role="tab"
        aria-selected={effectiveScope === "outlet"}
        className={`sr-history-scope-btn ${effectiveScope === "outlet" ? "is-active" : ""}`}
        onClick={() => setScope("outlet")}
      >
        {t("se.stockRequest.history.scopeOutlet", {
          defaultValue: "This outlet",
        })}
      </button>
      <button
        type="button"
        role="tab"
        aria-selected={effectiveScope === "all"}
        className={`sr-history-scope-btn ${effectiveScope === "all" ? "is-active" : ""}`}
        onClick={() => setScope("all")}
      >
        {t("se.stockRequest.history.scopeAll", {
          defaultValue: "All outlets",
        })}
      </button>
    </div>
  ) : null;

  return (
    <div className="sr-history">
      {scopeToggle}
      {loading ? (
        <div className="sr-history-empty">
          {t("se.stockRequest.history.loading", { defaultValue: "Loading..." })}
        </div>
      ) : error ? (
        <div className="form-error">{error}</div>
      ) : requests.length === 0 ? (
        <div className="sr-history-empty">
          <Package size={28} aria-hidden="true" />
          <p>
            {effectiveScope === "all"
              ? t("se.stockRequest.history.empty", {
                  defaultValue: "You have not requested any stock yet.",
                })
              : t("se.stockRequest.history.emptyForOutlet", {
                  defaultValue: "No stock requests for this outlet yet.",
                })}
          </p>
        </div>
      ) : (
        requests.map((request) => {
        const items = Array.isArray(request.items) ? request.items : [];
        const totalUnits = items.reduce(
          (sum, item) => sum + (Number.parseInt(item.quantity, 10) || 0),
          0,
        );
        const isPending = request.status === "pending";
        const canRequestAgain =
          request.status === "rejected" || request.status === "cancelled";

        return (
          <div
            key={request.stock_request_id}
            className="sale-item-section sale-item-grid-section sr-history-card"
          >
            <div className="sr-history-card-head">
              <div>
                <strong className="sr-history-id">
                  {request.order_no || `#${request.stock_request_id}`}
                </strong>
                <span className="sr-history-date">
                  {formatDateTime(request.requested_at)}
                </span>
              </div>
              <span className={`sr-history-badge is-${request.status}`}>
                {t(`se.stockRequest.history.status.${request.status}`, {
                  defaultValue: request.status,
                })}
              </span>
            </div>

            <div className="sr-history-meta">
              {request.outlet?.bp_outlet_name || "-"}
              {request.warehouse ? (
                <span className="sr-history-warehouse">
                  {" · "}
                  {[
                    request.warehouse.warehouse_code,
                    request.warehouse.warehouse_name,
                  ]
                    .filter(Boolean)
                    .join(" ")}
                </span>
              ) : null}
            </div>

            {request.resubmittedFrom ? (
              <div className="sr-history-meta">
                {t("se.stockRequest.history.resubmittedFrom", {
                  id:
                    request.resubmittedFrom.order_no ||
                    `#${request.resubmittedFrom.stock_request_id}`,
                  defaultValue: `Resubmitted from ${
                    request.resubmittedFrom.order_no ||
                    `#${request.resubmittedFrom.stock_request_id}`
                  }`,
                })}
              </div>
            ) : null}

            <ul className="sr-history-items">
              {items.map((item) => (
                <li key={item.stock_request_item_id}>
                  <span>{item.model_name || item.product_name || "-"}</span>
                  <strong>× {item.quantity}</strong>
                </li>
              ))}
            </ul>

            <div className="sr-history-total">
              {t("se.stockRequest.history.productCount", {
                count: items.length,
              })}
              {" · "}
              {t("se.stockRequest.history.unitCount", { count: totalUnits })}
            </div>

            {request.notes ? (
              <div className="sr-history-note">
                <span className="sr-history-note-label">
                  {t("se.stockRequest.history.yourNote", {
                    defaultValue: "Your note",
                  })}
                </span>
                {request.notes}
              </div>
            ) : null}

            {/* The admin's reason is the whole point of showing history —
                it tells the promoter what to change before asking again. */}
            {request.review_notes ? (
              <div
                className={`sr-history-note ${
                  request.status === "rejected" ? "is-rejection" : ""
                }`}
              >
                <span className="sr-history-note-label">
                  {request.status === "rejected"
                    ? t("se.stockRequest.history.rejectionReason", {
                        defaultValue: "Reason for rejection",
                      })
                    : t("se.stockRequest.history.adminNote", {
                        defaultValue: "Admin note",
                      })}
                </span>
                {request.review_notes}
              </div>
            ) : null}

            {(isPending || canRequestAgain) && (
              <div className="sr-history-actions">
                {isPending && (
                  <button
                    type="button"
                    className="btn-cancel sr-history-btn"
                    onClick={() => setCancelTarget(request)}
                    disabled={cancellingId === request.stock_request_id}
                  >
                    <XCircle size={15} aria-hidden="true" />
                    {cancellingId === request.stock_request_id
                      ? t("se.stockRequest.history.cancelling", {
                          defaultValue: "Cancelling...",
                        })
                      : t("se.stockRequest.history.cancel", {
                          defaultValue: "Cancel request",
                        })}
                  </button>
                )}
                {canRequestAgain && (
                  <button
                    type="button"
                    className="btn-submit sr-history-btn"
                    onClick={() => onRequestAgain?.(request)}
                  >
                    <RotateCcw size={15} aria-hidden="true" />
                    {t("se.stockRequest.history.requestAgain", {
                      defaultValue: "Request again",
                    })}
                  </button>
                )}
              </div>
            )}
          </div>
        );
        })
      )}

      {cancelTarget && (
        <div className="se-dialog-overlay" role="dialog" aria-modal="true">
          <div className="se-dialog">
            <div className="se-dialog-header">
              <h3 className="se-dialog-title">
                {t("se.stockRequest.history.cancelConfirmTitle", {
                  defaultValue: "Cancel this request?",
                })}
              </h3>
              <button
                type="button"
                onClick={() => setCancelTarget(null)}
                className="se-dialog-close"
                disabled={cancellingId === cancelTarget.stock_request_id}
                aria-label={t("se.stockRequest.history.keepRequest", {
                  defaultValue: "Keep request",
                })}
              >
                ×
              </button>
            </div>
            <div className="se-dialog-body">
              <p className="se-dialog-message">
                {t("se.stockRequest.history.cancelConfirmMessage", {
                  id:
                    cancelTarget.order_no ||
                    `#${cancelTarget.stock_request_id}`,
                  defaultValue: `Request ${
                    cancelTarget.order_no ||
                    `#${cancelTarget.stock_request_id}`
                  } will be withdrawn and can no longer be approved. You can submit a new request later.`,
                })}
              </p>
            </div>
            <div className="se-dialog-actions">
              <button
                type="button"
                onClick={() => setCancelTarget(null)}
                className="se-dialog-btn se-dialog-btn-cancel"
                disabled={cancellingId === cancelTarget.stock_request_id}
              >
                {t("se.stockRequest.history.keepRequest", {
                  defaultValue: "Keep request",
                })}
              </button>
              <button
                type="button"
                onClick={handleConfirmCancel}
                className="se-dialog-btn se-dialog-btn-confirm"
                disabled={cancellingId === cancelTarget.stock_request_id}
              >
                {cancellingId === cancelTarget.stock_request_id
                  ? t("se.stockRequest.history.cancelling", {
                      defaultValue: "Cancelling...",
                    })
                  : t("se.stockRequest.history.cancelConfirmBtn", {
                      defaultValue: "Yes, cancel request",
                    })}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default StockRequestHistory;
